import { useQuery } from '@tanstack/react-query';
import { Presentation } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDate } from '@/utils/format';
import { PortalShell, type PortalContext } from './PortalShell';
import { SectionTitle, WeekGrid, isNotFound } from './shared';

/** The published timetable in force today for the student's class, as a week. */
export function PortalTimetablePage() {
  return (
    <PortalShell title="Timetable" icon={<Presentation className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <Timetable context={context} />}
    </PortalShell>
  );
}

function Timetable({ context }: { context: PortalContext }) {
  const { subject, subjectKey } = context;

  const timetable = useQuery({
    queryKey: queryKeys.portal.timetable(subjectKey),
    queryFn: () => portalApi.timetable(subject),
    retry: false,
  });

  if (timetable.isPending) {
    return (
      <Card>
        <LoadingState label="Loading the timetable" />
      </Card>
    );
  }

  if (timetable.isError) {
    return (
      <Card>
        {isNotFound(timetable.error) ? (
          <EmptyState
            icon={<Presentation className="h-6 w-6" aria-hidden="true" />}
            title="No timetable has been published"
            description="The school has not published a timetable for this class yet."
          />
        ) : (
          <ErrorState error={timetable.error} onRetry={() => void timetable.refetch()} />
        )}
      </Card>
    );
  }

  const data = timetable.data;

  return (
    <Card>
      <SectionTitle>
        {[data.name, data.sectionName].filter(Boolean).join(' · ') || 'This week'}
      </SectionTitle>
      <p className="px-4 pt-2 text-xs text-muted">
        In force from {formatDate(data.effectiveFrom)}
        {data.effectiveTo !== undefined && ` until ${formatDate(data.effectiveTo)}`}
      </p>
      {data.entries.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted">This timetable has no lessons yet.</p>
      ) : (
        <WeekGrid
          entries={data.entries}
          renderCell={(entry) => (
            <span className="block">
              <span className="block text-sm font-semibold text-ink">{entry.subjectName ?? '—'}</span>
              <span className="block text-[11px] text-muted">
                {[entry.teacherName, entry.room].filter(Boolean).join(' · ')}
              </span>
            </span>
          )}
        />
      )}
    </Card>
  );
}
